import React, { useState, useEffect, useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { LoadingState, EmptyState, Pagination, ConfirmationModal } from '../../components/common';
import { Button } from '../../components/common/Button';
import { Card, CardContent } from '../../components/common/Card';
import { Modal } from '../../components/modals/Modal';
import { SchoolForm } from '../../components/schools/SchoolForm';
import { Table } from '../../components/tables/Table';
import { APP_PERMISSIONS } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';
import {
  School,
  getSchools,
  createSchool,
  updateSchool,
  deleteSchool,
} from '../../services/schoolsService';
import { SchoolCreateFormData, SchoolUpdateFormData } from '../../types';

interface SearchForm {
  search: string;
}

export const SchoolsPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState<School | null>(null);
  const [deletingSchool, setDeletingSchool] = useState<School | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
    next_page: null as number | null,
    prev_page: null as number | null
  });
  const [itemsPerPage] = useState(10); // 10 items per page

  // Get current page and search from URL params
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const urlSearchTerm = searchParams.get('search') || '';

  // Search form
  const { register, handleSubmit, reset } = useForm<SearchForm>({
    defaultValues: { search: urlSearchTerm },
  });

  // Load schools
  const loadSchools = useCallback(async (
    page: number = currentPage,
    search: string = urlSearchTerm
  ) => {
    try {
      setLoading(true);
      setError(null);

      const skip = (page - 1) * itemsPerPage;
      const response = await getSchools({
        skip,
        limit: itemsPerPage,
        search: search || undefined,
      });

      // Handle the new paginated response structure
      const schoolsData = response.data || [];
      const paginationData = response.pagination || {
        page: 1,
        per_page: 10,
        total: 0,
        pages: 1,
        has_next: false,
        has_prev: false,
        next_page: null,
        prev_page: null,
      };

      setSchools(schoolsData);
      setPagination(paginationData);
    } catch (err) {
      setError('Failed to load schools');
      console.error('Error loading schools:', err);
      setSchools([]);
      setPagination({
        page: 1,
        per_page: 10,
        total: 0,
        pages: 1,
        has_next: false,
        has_prev: false,
        next_page: null,
        prev_page: null
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, urlSearchTerm]);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  // Handle search form submission
  const handleSearch = (data: SearchForm) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (data.search.trim()) {
      newSearchParams.set('search', data.search.trim());
    } else {
      newSearchParams.delete('search');
    }
    newSearchParams.set('page', '1'); // Reset to first page when searching
    setSearchParams(newSearchParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', page.toString());
      setSearchParams(newSearchParams);
    }
  };

  // Modal handlers
  const handleOpenModal = () => {
    setEditingSchool(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSchool(null);
  };

  // const handleViewSchool = (school: School) => {
  //   // TODO: Implement view functionality
  //   toast.info(`Viewing school: ${school.name}`);
  // };

  const handleEditSchool = (school: School) => {
    setEditingSchool(school);
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (
    formData: SchoolCreateFormData | SchoolUpdateFormData
  ) => {
    try {
      setIsSubmitting(true);

      if (editingSchool) {
        await updateSchool(editingSchool.id, formData as SchoolUpdateFormData);
        toast.success('School updated successfully');
      } else {
        await createSchool(formData as SchoolCreateFormData);
        toast.success('School created successfully');
      }

      handleCloseModal();
      loadSchools(); // Reload current page
    } catch (err) {
      console.error('Error saving school:', err);
      toast.error(
        editingSchool ? 'Failed to update school' : 'Failed to create school'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchool = (school: School) => {
    setDeletingSchool(school);
  };

  const confirmDelete = async () => {
    if (!deletingSchool) return;

    try {
      await deleteSchool(deletingSchool.id);
      toast.success('School deleted successfully');
      setDeletingSchool(null);
      loadSchools(); // Reload current page
    } catch (err) {
      console.error('Error deleting school:', err);
      toast.error('Failed to delete school');
    }
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'School Name',
      accessor: (school: School) => (
        <div>
          <div className="font-medium text-gray-900">{school.name}</div>
          <div className="text-sm text-gray-500">{school.code}</div>
        </div>
      ),
    },
    {
      key: 'school_type',
      header: 'Type',
      accessor: (school: School) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {school.school_type}
          </div>
          <div className="text-sm text-gray-500">{school.grade_levels}</div>
        </div>
      ),
    },
    {
      key: 'district',
      header: 'District',
      accessor: (school: School) => (
        <div className="text-sm text-gray-900">
          {school.district?.name || 'N/A'}
        </div>
      ),
    },
    {
      key: 'principal',
      header: 'Principal',
      accessor: (school: School) => (
        <div className="text-sm text-gray-900">
          {school.principal || 'N/A'}
        </div>
      ),
    },
    {
      key: 'student_count',
      header: 'Student Count',
      accessor: (school: School) => (
        <div className="text-sm text-gray-900">
          {school.student_count ? school.student_count.toLocaleString() : 'N/A'}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (school: School) => (
        <div className="flex items-center space-x-2">
          {/* {hasPermission(APP_PERMISSIONS.READ_SCHOOL) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleViewSchool(school)}
              className="h-8 w-8 p-0"
            >
              👁️
            </Button>
          )} */}
          {hasPermission(APP_PERMISSIONS.UPDATE_SCHOOL) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditSchool(school)}
              className="h-8 w-8 p-0"
            >
              ✏️
            </Button>
          )}
          {hasPermission(APP_PERMISSIONS.DELETE_SCHOOL) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteSchool(school)}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            >
              🗑️
            </Button>
          )}
        </div>
      ),
    },
  ];

  const canRead = hasPermission(APP_PERMISSIONS.READ_SCHOOL);
  const canCreate = hasPermission(APP_PERMISSIONS.CREATE_SCHOOL);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Schools Management
          </h1>
          <p className="text-gray-600">
            Manage schools and organizational structure
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleOpenModal} variant="primary">
            + Add School
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(handleSearch)} className="flex gap-4">
            <div className="flex-1">
              <input
                {...register('search')}
                type="text"
                placeholder="Search schools by name, code, type, or grade levels..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                defaultValue={urlSearchTerm}
              />
            </div>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </Button>
            {urlSearchTerm && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  reset({ search: '' });
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.delete('search');
                  newSearchParams.set('page', '1');
                  setSearchParams(newSearchParams);
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Schools Table */}
      {canRead ? (
        <div className="space-y-4">
          <Card>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingState message="Loading schools..." />
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-red-600 mb-4">
                    <h3 className="text-lg font-semibold">
                      Failed to load schools
                    </h3>
                    <p className="text-sm">{error}</p>
                  </div>
                  <Button onClick={() => loadSchools()} variant="secondary">
                    Try Again
                  </Button>
                </div>
              ) : schools.length === 0 ? (
                <EmptyState
                  title="No schools found"
                  description="There are no schools to display. Create your first school to get started."
                  icon="🏫"
                  actionText={canCreate ? 'Add School' : undefined}
                  onAction={canCreate ? handleOpenModal : undefined}
                />
              ) : (
                <Table data={schools} columns={columns} keyField="id" />
              )}
            </CardContent>
          </Card>

          {/* Pagination - Below the table */}
          {!loading && !error && (
            <div className="bg-white px-4 py-3 border border-gray-200 rounded-lg">
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      ) : (
        <Card>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-gray-500">
                You don't have permission to view schools.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingSchool ? 'Edit School' : 'Create School'}
        size="xl"
      >
        <SchoolForm
          school={editingSchool || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseModal}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deletingSchool}
        onClose={() => setDeletingSchool(null)}
        onConfirm={confirmDelete}
        title="Delete School"
        message={`Are you sure you want to delete ${deletingSchool?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default SchoolsPage;
